import React from 'react';
import {
  LayoutDashboard,
  CreditCard,
  Package,
  Terminal,
  ShoppingCart,
  Truck,
  Users,
  Settings,
  Database,
  BarChart3,
  Scissors,
  Globe,
  FileJson,
  Briefcase,
  Server,
  FileText,
  Hash,
  Layers,
  Building2,
  DollarSign,
} from 'lucide-react';
import { ModuleId } from '@/types';

export const NAVIGATION_ITEMS = [
  {
    id: 'DASHBOARD',
    label: 'Dashboard',
    icon: <LayoutDashboard size={20} />,
    category: 'General',
    description: 'Executive summary and SMS configuration.',
    sections: [
      {
        label: 'Overview',
        items: [
          { id: 'dash-main', label: 'Dashboard', moduleId: ModuleId.DASHBOARD },
          { id: 'dash-sms', label: 'SMS Configuration', moduleId: ModuleId.DASHBOARD },
        ],
      },
    ],
  },
  {
    id: 'FINANCE',
    label: 'Financial Accounting',
    icon: <DollarSign size={20} />,
    category: 'Finance',
    description: 'Chart of Accounts, Ledgers, and Financial Management.',
    sections: [
      {
        label: 'Masters',
        items: [
          { id: 'fin-coa', label: 'Chart of Accounts', moduleId: ModuleId.CHART_OF_ACCOUNTS },
          { id: 'fin-pg', label: 'Primary Groups', moduleId: ModuleId.PRIMARY_GROUP },
          { id: 'fin-gl', label: 'Group Ledgers', moduleId: ModuleId.GROUP_LEDGER },
          { id: 'fin-gen', label: 'General Ledgers', moduleId: ModuleId.GENERAL_LEDGER },
        ],
      },
      {
        label: 'Receipts',
        items: [
          { id: 'fin-cash-rcpt', label: 'Cash Receipt', moduleId: ModuleId.CASH_RECEIPT },
          { id: 'fin-bank-rcpt', label: 'Bank Receipt', moduleId: ModuleId.BANK_RECEIPT },
        ],
      },
      {
        label: 'Payments',
        items: [
          { id: 'fin-cash-pay', label: 'Cash Payment', moduleId: ModuleId.CASH_PAYMENT },
          { id: 'fin-bank-pay', label: 'Bank Payment', moduleId: ModuleId.BANK_PAYMENT },
        ],
      },
      {
        label: 'Journals',
        items: [
          { id: 'fin-jnl', label: 'Journal Entry', moduleId: ModuleId.JOURNAL_ENTRY },
        ],
      },
      {
        label: 'Reports',
        items: [
          { id: 'fin-tb', label: 'Trial Balance', moduleId: ModuleId.TRIAL_BALANCE },
          { id: 'fin-pl', label: 'Profit & Loss', moduleId: ModuleId.PROFIT_LOSS },
          { id: 'fin-bs', label: 'Balance Sheet', moduleId: ModuleId.BALANCE_SHEET },
          { id: 'fin-brs', label: 'Bank Reconciliation', moduleId: ModuleId.BANK_RECONCILIATION },
        ],
      },
    ],
  },
  {
    id: 'MYACCOUNTS_SETUP',
    label: 'MyAccounts Setup',
    icon: <Server size={20} />,
    category: 'Setup',
    hideInSidebar: true,
    description: 'Core server, warehouse, and client configuration.',
    sections: [
      {
        label: 'Systems Server Setup',
        items: [
          { id: 'mas-srv', label: 'Server', moduleId: ModuleId.MYACCOUNTS_SETUP },
          { id: 'mas-wh', label: 'Warehouse', moduleId: ModuleId.MYACCOUNTS_SETUP },
          { id: 'mas-br', label: 'Branches', moduleId: ModuleId.MYACCOUNTS_SETUP },
        ],
      },
      {
        label: 'General',
        items: [
          { id: 'mas-cli', label: 'Client Information', moduleId: ModuleId.MYACCOUNTS_SETUP },
        ],
      },
    ],
  },
  {
    id: 'ATTRIBUTE_STUDIO',
    label: 'Attribute & Pricing Studio',
    icon: <Settings size={20} />,
    category: 'Setup',
    description: 'Configure product attributes per industry and pricing fields per country — the master library that drives Item Master and transaction screens.',
    sections: [
      {
        label: 'Configuration',
        items: [
          { id: 'attr-studio-main', label: 'Attribute & Pricing Studio', moduleId: ModuleId.ATTRIBUTE_STUDIO },
        ],
      },
    ],
  },
  {
    id: 'SYSTEM_MANAGER',
    label: 'System Manager',
    icon: <Settings size={20} />,
    category: 'Setup',
    hideInSidebar: true,
    description: 'User UAMS, modules, and system audits.',
    sections: [
      {
        label: 'USER UAMS',
        items: [
          { id: 'sm-uams-grp', label: 'User Groups', moduleId: ModuleId.SYSTEM_MANAGER },
          { id: 'sm-uams-role', label: 'User Roles', moduleId: ModuleId.SYSTEM_MANAGER },
          { id: 'sm-uams-lvl', label: 'User Levels', moduleId: ModuleId.SYSTEM_MANAGER },
          { id: 'sm-uams-cre', label: 'User Creation', moduleId: ModuleId.SYSTEM_MANAGER },
          { id: 'sm-uams-priv', label: 'User Privilege (Profile)', moduleId: ModuleId.SYSTEM_MANAGER },
          { id: 'sm-uams-ctrl', label: 'Control Management', moduleId: ModuleId.SYSTEM_MANAGER },
          { id: 'sm-uams-trc', label: 'Event Trace Report', moduleId: ModuleId.SYSTEM_MANAGER },
        ],
      },
      {
        label: 'Setup',
        items: [
          { id: 'sm-set-mod', label: 'Modules', moduleId: ModuleId.SYSTEM_MANAGER },
          { id: 'sm-set-sub', label: 'Sub Modules', moduleId: ModuleId.SYSTEM_MANAGER },
          { id: 'sm-set-scr', label: 'User Screens', moduleId: ModuleId.SYSTEM_MANAGER },
          { id: 'sm-set-vert', label: 'Vertical Master Setup', moduleId: ModuleId.SYSTEM_MANAGER },
          { id: 'sm-set-tb', label: 'ToolBar Buttons', moduleId: ModuleId.SYSTEM_MANAGER },
          { id: 'sm-set-ftp', label: 'FTP Permissions', moduleId: ModuleId.SYSTEM_MANAGER },
        ],
      },
      {
        label: 'Operations',
        items: [
          { id: 'sm-ops-bd', label: 'Business Days', moduleId: ModuleId.SYSTEM_MANAGER },
          { id: 'sm-ops-do', label: 'Day Opening', moduleId: ModuleId.SYSTEM_MANAGER },
          { id: 'sm-ops-dc', label: 'Day Closing', moduleId: ModuleId.SYSTEM_MANAGER },
        ],
      },
      {
        label: 'Reporting & DB',
        items: [
          { id: 'sm-rep', label: 'Reports', moduleId: ModuleId.SYSTEM_MANAGER },
          { id: 'sm-db-res', label: 'Restore Database', moduleId: ModuleId.SYSTEM_MANAGER },
          { id: 'sm-db-bak', label: 'Schedule Backup', moduleId: ModuleId.SYSTEM_MANAGER },
        ],
      },
      {
        label: 'Audit',
        items: [
          { id: 'sm-aud-log', label: 'User Login Info', moduleId: ModuleId.SYSTEM_MANAGER },
          { id: 'sm-aud-ops', label: 'User Operations', moduleId: ModuleId.SYSTEM_MANAGER },
          { id: 'sm-aud-txn', label: 'Transaction Audit', moduleId: ModuleId.SYSTEM_MANAGER },
        ],
      },
    ],
  },
  {
    id: 'APP_SETUP',
    label: 'Application Setup',
    icon: <Briefcase size={20} />,
    category: 'Setup',
    hideInSidebar: true,
    description: 'Organization, module settings, and regional configs.',
    sections: [
      {
        label: 'Primary Organization',
        items: [
          { id: 'as-po-ea', label: 'Enterprise Account', moduleId: ModuleId.APP_SETUP },
          { id: 'as-po-gc', label: 'Group Company', moduleId: ModuleId.APP_SETUP },
          { id: 'as-po-gm', label: 'Group Master', moduleId: ModuleId.APP_SETUP },
          { id: 'as-po-br', label: 'Branch Master', moduleId: ModuleId.APP_SETUP },
          { id: 'as-po-wh', label: 'Warehouse Master', moduleId: ModuleId.APP_SETUP },
        ],
      },
      {
        label: 'Regional',
        items: [
          { id: 'as-reg-cnt', label: 'Country', moduleId: ModuleId.APP_SETUP },
          { id: 'as-reg-st', label: 'State', moduleId: ModuleId.APP_SETUP },
          { id: 'as-reg-ct', label: 'City', moduleId: ModuleId.APP_SETUP },
        ],
      },
      {
        label: 'Company',
        items: [
          { id: 'as-cmp-co', label: 'Company', moduleId: ModuleId.APP_SETUP },
          { id: 'as-cmp-bc', label: 'Books Closing', moduleId: ModuleId.APP_SETUP },
          { id: 'as-cmp-yc', label: 'Year Closing', moduleId: ModuleId.APP_SETUP },
        ],
      },
      {
        label: 'Application Settings',
        items: [
          { id: 'as-set-pos', label: 'Point of Sale', moduleId: ModuleId.APP_SETUP },
          { id: 'as-set-str', label: 'Store Management', moduleId: ModuleId.APP_SETUP },
          { id: 'as-set-pro', label: 'Procurement', moduleId: ModuleId.APP_SETUP },
          { id: 'as-set-inv', label: 'Inventory', moduleId: ModuleId.APP_SETUP },
          { id: 'as-set-sal', label: 'Sales & Distribution', moduleId: ModuleId.APP_SETUP },
          { id: 'as-set-prd', label: 'Production', moduleId: ModuleId.APP_SETUP },
          { id: 'as-set-mul', label: 'Multi Store Distribution', moduleId: ModuleId.APP_SETUP },
          { id: 'as-set-gen', label: 'General', moduleId: ModuleId.APP_SETUP },
          { id: 'as-set-ql', label: 'Quick Launch', moduleId: ModuleId.APP_SETUP },
          { id: 'as-set-acc', label: 'Accounts Module', moduleId: ModuleId.APP_SETUP },
          { id: 'as-set-pay', label: 'Payroll', moduleId: ModuleId.APP_SETUP },
          { id: 'as-set-log', label: 'Logistic', moduleId: ModuleId.APP_SETUP },
          { id: 'as-set-shp', label: 'Shopping Shop', moduleId: ModuleId.APP_SETUP },
          { id: 'as-set-fra', label: 'Franchises', moduleId: ModuleId.APP_SETUP },
          { id: 'as-set-so', label: 'Sales Order', moduleId: ModuleId.APP_SETUP },
          { id: 'as-set-ds', label: 'Departmental Stores', moduleId: ModuleId.APP_SETUP },
          { id: 'as-set-ye', label: 'Year End', moduleId: ModuleId.APP_SETUP },
          { id: 'as-set-pp', label: 'Product Profitability', moduleId: ModuleId.APP_SETUP },
          { id: 'as-set-sms', label: 'SMS & Mail Config', moduleId: ModuleId.APP_SETUP },
        ],
      },
      {
        label: 'Configurations',
        items: [
          { id: 'as-cfg-att', label: 'Attribute Setting', moduleId: ModuleId.APP_SETUP },
          { id: 'as-cfg-cl', label: 'Control List Config', moduleId: ModuleId.APP_SETUP },
          { id: 'as-cfg-rl', label: 'Report List Config', moduleId: ModuleId.APP_SETUP },
          { id: 'as-cfg-rs', label: 'Report Settings', moduleId: ModuleId.APP_SETUP },
          { id: 'as-cfg-sid', label: 'Screen ID Setup', moduleId: ModuleId.APP_SETUP },
          { id: 'as-cfg-bfs', label: 'Bill Format Setup', moduleId: ModuleId.APP_SETUP },
          { id: 'as-cfg-set', label: 'Setup (Barcode/Weigh)', moduleId: ModuleId.APP_SETUP },
          { id: 'as-cfg-rps', label: 'Report Setup', moduleId: ModuleId.APP_SETUP },
        ],
      },
    ],
  },
  {
    id: 'DATA_MGMT',
    label: 'Data Management',
    icon: <FileJson size={20} />,
    category: 'Setup',
    hideInSidebar: true,
    description: 'Data import/export and third-party integrations.',
    sections: [
      {
        label: 'Data Import',
        items: [
          { id: 'dm-imp-ma', label: 'MyAccounts Import', moduleId: ModuleId.DATA_MGMT },
          { id: 'dm-imp-3p', label: '3rd Party Import', moduleId: ModuleId.DATA_MGMT },
        ],
      },
      {
        label: 'Data Exports',
        items: [
          { id: 'dm-exp-tal', label: 'Tally Export', moduleId: ModuleId.DATA_MGMT },
        ],
      },
    ],
  },
  {
    id: 'FINAL_ACCOUNTS',
    label: 'Final Accounts Setup',
    icon: <BarChart3 size={20} />,
    category: 'Setup',
    hideInSidebar: true,
    description: 'Chart of accounts and financial mappings.',
    sections: [
      { label: 'Default Accounts', items: [{ id: 'fa-def-def', label: 'Default', moduleId: ModuleId.FINAL_ACCOUNTS }] },
      { label: 'Logistic Accounts', items: [{ id: 'fa-log-acc', label: 'Accounts', moduleId: ModuleId.FINAL_ACCOUNTS }] },
      { label: 'Procurement Accounts', items: [{ id: 'fa-pro-acc', label: 'Accounts Setup', moduleId: ModuleId.FINAL_ACCOUNTS }] },
      { label: 'POS Accounts', items: [{ id: 'fa-pos-sa', label: 'Sales A/c', moduleId: ModuleId.FINAL_ACCOUNTS }, { id: 'fa-pos-st', label: 'Sales Tax A/c', moduleId: ModuleId.FINAL_ACCOUNTS }] },
      { label: 'HR Accounts', items: [{ id: 'fa-hr-pay', label: 'Payroll', moduleId: ModuleId.FINAL_ACCOUNTS }] },
      { label: 'General Settings', items: [{ id: 'fa-gen-gen', label: 'General', moduleId: ModuleId.FINAL_ACCOUNTS }] },
      { label: 'External', items: [{ id: 'fa-tal', label: 'Tally', moduleId: ModuleId.FINAL_ACCOUNTS }] },
    ],
  },
  {
    id: 'WEB_SETUP',
    label: 'Web Application Setup',
    icon: <Globe size={20} />,
    category: 'Setup',
    hideInSidebar: true,
    description: 'Web application and server configurations.',
    sections: [
      { label: 'MyAccounts Server', items: [{ id: 'ws-srv-1', label: 'Server I', moduleId: ModuleId.WEB_SETUP }] },
    ],
  },
  {
    id: 'SETTINGS_REPORTS',
    label: 'Settings Reports',
    icon: <FileText size={20} />,
    category: 'Setup',
    hideInSidebar: true,
    description: 'System settings reports.',
    sections: [
      { label: 'User Reports', items: [{ id: 'sr-usr-priv', label: 'User Privileges Report', moduleId: ModuleId.SETTINGS_REPORTS }] },
    ],
  },
  {
    id: 'INVENTORY',
    label: 'Inventory',
    icon: <Package size={20} />,
    category: 'Operations',
    description: 'Stock management, transfers, and warehouse reports.',
    sections: [
      {
        label: 'Branch Transfers',
        items: [
          { id: 'inv-bt-1', label: 'Branch Sales Summary', moduleId: ModuleId.INVENTORY },
          { id: 'inv-bt-2', label: 'Stock Import from Branch', moduleId: ModuleId.INVENTORY },
          { id: 'inv-bt-3', label: 'Stock Transfer To Branch', moduleId: ModuleId.INVENTORY },
        ],
      },
      {
        label: 'Warehouse',
        items: [
          { id: 'inv-wh-1', label: 'Opening Stock Import', moduleId: ModuleId.INVENTORY },
          { id: 'inv-wh-2', label: 'Stock Adjustment Entry', moduleId: ModuleId.INVENTORY },
          { id: 'inv-wh-3', label: 'Stock Verification (PDA)', moduleId: ModuleId.INVENTORY },
        ],
      },
    ],
  },
  {
    id: 'PURCHASE',
    label: 'Purchase',
    icon: <ShoppingCart size={20} />,
    category: 'Operations',
    description: 'Goods receipts, POs, and supplier management.',
    sections: [
      {
        label: 'Purchase Invoices',
        items: [
          { id: 'pur-inv-1', label: 'Purchase Invoice', moduleId: ModuleId.PURCHASE_INVOICE },
          { id: 'pur-grn-1', label: 'GRN Entry', moduleId: ModuleId.GRN },
          { id: 'pur-ret-1', label: 'Purchase Return', moduleId: ModuleId.PURCHASE_RETURN },
        ],
      },
      {
        label: 'Goods Receipts (GRC)',
        items: [
          { id: 'pur-grc-1', label: 'GRC (Direct)', moduleId: ModuleId.PURCHASE },
          { id: 'pur-grc-2', label: 'GRC (Garments)', moduleId: ModuleId.PURCHASE },
        ],
      },
      {
        label: 'Purchase Orders',
        items: [
          { id: 'pur-po-1', label: 'Purchase Order', moduleId: ModuleId.PURCHASE },
          { id: 'pur-po-4', label: 'PO Import from Excel', moduleId: ModuleId.PURCHASE },
        ],
      },
    ],
  },
  {
    id: 'POS',
    label: 'Point of Sale',
    icon: <CreditCard size={20} />,
    category: 'Sales',
    description: 'POS Masters, Billing, Orders, Delivery, Invoice and Reports.',
    sections: [
      {
        label: 'Masters',
        items: [
          { id: 'pos-floor', label: 'Floor Master', moduleId: ModuleId.FLOOR_MASTER },
          { id: 'pos-counter', label: 'Counter Master', moduleId: ModuleId.COUNTER_MASTER },
          { id: 'pos-place', label: 'Place Master', moduleId: ModuleId.PLACE_MASTER },
          { id: 'pos-shift', label: 'Shift Master', moduleId: ModuleId.SHIFT_MASTER },
          { id: 'pos-salesman', label: 'Salesman Master', moduleId: ModuleId.SALESMAN_MASTER },
          { id: 'pos-customer', label: 'Customer Master', moduleId: ModuleId.CUSTOMER_MASTER },
          { id: 'pos-payment', label: 'Payment Master', moduleId: ModuleId.PAYMENT_MASTER },
          { id: 'pos-promotion', label: 'Promotion Master', moduleId: ModuleId.PROMOTION_MASTER },
        ],
      },
      {
        label: 'Orders',
        items: [
          { id: 'pos-so', label: 'Sales Order', moduleId: ModuleId.SALES_ORDER },
        ],
      },
      {
        label: 'Delivery Challan',
        items: [
          { id: 'pos-dc', label: 'Delivery Challan', moduleId: ModuleId.DELIVERY_CHALLAN },
          { id: 'pos-dcr', label: 'DC Returns', moduleId: ModuleId.DC_RETURNS },
        ],
      },
      {
        label: 'Sales Invoice',
        items: [
          { id: 'pos-inv', label: 'Sales Invoice', moduleId: ModuleId.SALES_INVOICE },
        ],
      },
      {
        label: 'Billing',
        items: [
          { id: 'pos-billing', label: 'POS Billing', moduleId: ModuleId.POS_BILLING },
        ],
      },
      {
        label: 'Reports',
        items: [
          { id: 'pos-rpt-summary', label: 'Sales Summary Report', moduleId: ModuleId.POS_SALES_SUMMARY },
          { id: 'pos-rpt-b2b', label: 'Bill to Bill Report', moduleId: ModuleId.POS_BILL_TO_BILL },
        ],
      },
    ],
  },
  {
    id: 'LOGISTICS',
    label: 'Logistics',
    icon: <Truck size={20} />,
    category: 'Operations',
    description: 'Transport, gate pass, and bale management.',
    sections: [
      { label: 'Transit', items: [{ id: 'log-tr-1', label: 'Bills Transit', moduleId: ModuleId.LOGISTICS }] },
    ],
  },
  {
    id: 'MASTERS',
    label: 'Masters',
    icon: <Database size={20} />,
    category: 'Operations',
    description: 'System-wide master data management.',
    sections: [
      { 
        label: 'Product Masters', 
        items: [
          { id: 'mas-item-create', label: 'Item / Product Creation', moduleId: ModuleId.ITEM_CREATION },
          { id: 'mas-prod-3', label: 'Product Master', moduleId: ModuleId.MASTERS },
        ] 
      },
    ],
  },
  {
    id: 'PAYROLL',
    label: 'Payroll',
    icon: <Users size={20} />,
    category: 'HR',
    description: 'Employee management and salary processing.',
    sections: [
      { label: 'Employees', items: [{ id: 'pay-emp-1', label: 'Employees Master', moduleId: ModuleId.PAYROLL }] },
    ],
  },
  {
    id: 'PRODUCTION',
    label: 'Job Work',
    icon: <Scissors size={20} />,
    category: 'Manufacturing',
    description: 'Production, job work, and bill of materials.',
    sections: [
      { label: 'Processes', items: [{ id: 'prd-proc-1', label: 'Bill of Material', moduleId: ModuleId.PRODUCTION }] },
    ],
  },
  {
    id: 'KERNEL',
    label: 'System Kernel',
    icon: <Terminal size={20} />,
    category: 'Admin',
    description: 'Core system monitoring and configuration.',
    sections: [
      {
        label: 'Monitoring',
        items: [
          { id: 'sys-flt', label: 'Fault Audit', moduleId: ModuleId.FAULT_AUDIT },
          { id: 'sys-msh', label: 'Mesh Topology', moduleId: ModuleId.INFRA_MESH },
          { id: 'sys-lic', label: 'License Desk', moduleId: ModuleId.LICENSE_KERNEL },
        ],
      },
      {
        label: 'Security',
        items: [
          { id: 'sys-sec-1', label: 'Identity Engine', moduleId: ModuleId.IDENTITY },
        ],
      },
    ],
  },
  {
    id: 'ULID',
    label: 'ULID Generator',
    icon: <Hash size={20} />,
    category: 'Tools',
    description: 'Enterprise ULID generation with blockchain integrity.',
    sections: [
      {
        label: 'ID Generation',
        items: [
          { id: 'ulid-gen', label: 'Generate ULID', moduleId: ModuleId.ULID_GENERATOR },
          { id: 'ulid-decode', label: 'Decode ULID', moduleId: ModuleId.ULID_GENERATOR },
          { id: 'ulid-bench', label: 'Benchmark', moduleId: ModuleId.ULID_GENERATOR },
          { id: 'ulid-verify', label: 'Verify Chain', moduleId: ModuleId.ULID_GENERATOR },
        ],
      },
    ],
  },
];

export const CURRENCIES = ['USD', 'EUR', 'INR', 'GBP', 'JPY', 'CNY'];
export const LANGUAGES = ['English', 'Spanish', 'French', 'German'];
